import { Avatar, Box, Flex, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { isAuthenticated } from '@partiu/api/axios';
import { PropsWithChildren, useMemo } from 'react';
import { FaEnvelope } from 'react-icons/fa';
import { GoBell, GoPerson, GoStar } from 'react-icons/go';
import { RiHome2Line } from 'react-icons/ri';
import { TbApps } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import { BottomMenu, IBottomMenuLink } from '../../../../packages/ui/components/organisms/BottomMenu';
import { NotificationsDrawer } from '../../../../packages/ui/components/organisms/NotificationsDrawer';
import { TopMenu } from '../../../../packages/ui/components/organisms/TopMenu';
import { useProfile } from '../../../../packages/ui/contexts/ProfileProvider';
import { useTheme } from '../../../../packages/ui/contexts/ThemeProvider';
import { BookModal } from '../components/organisms/BookModal';
import { OpenBookProvider } from '../contexts/BookContext';

export function MainTemplate({
  hideTopMenu = false,
  hideBottomMenu = false,
  noShadow = false,
  children,
}: { hideTopMenu?: boolean; hideBottomMenu?: boolean; noShadow?: boolean } & PropsWithChildren) {
  const theme = useTheme();

  const { profile } = useProfile();
  const notificationDisclosure = useDisclosure();

  const links = useMemo<IBottomMenuLink[]>(() => {
    if (isAuthenticated()) {
      return [
        { name: 'Home', icon: <RiHome2Line />, path: '/' },
        {
          name: theme.bookstoreTypes.includes('magazine') ? 'Banca' : 'Acervo',
          icon: <TbApps />,
          path:  theme.bookstoreTypes.includes('magazine') ? '/banca' : '/acervo',
        },
        {
          name: theme.bookstoreTypes.includes('magazine') ? 'Minhas Revistas' : 'Minha Biblioteca',
          icon: <GoStar />,
          path:  theme.bookstoreTypes.includes('magazine') ? '/revistas' : '/biblioteca',
        },
        { name: 'Fale Conosco', icon: <FaEnvelope />, path: '/contato', desktopName: 'Fale Conosco', hide: true },
        { name: 'Perfil', icon: <GoPerson />, path: '/perfil', desktopName: 'Meus Dados' },
      ];
    } else {
      return [
        { name: 'Home', icon: <RiHome2Line />, path: '/' },
        { name: 'Fale Conosco', icon: <FaEnvelope />, path: '/contato', desktopName: 'Fale Conosco', hide: true },
      ];
    }
  }, [theme]);

  const basePaddingTop = useMemo(() => {
    let result = 0;
    if (!hideTopMenu) result += 60;
    if (theme.isNative) result = 0;

    return result + 'px';
  }, [hideTopMenu, theme]);

  return (
    <OpenBookProvider>
      <BookModal />
      {isAuthenticated() && <NotificationsDrawer {...notificationDisclosure} />}
      {!theme.isNative && (
        <Flex pos={'fixed'} zIndex={100} display={{ base: hideTopMenu ? 'none' : 'flex', lg: 'flex' }}>
          <TopMenu
            noShadow={noShadow}
            links={links.map((l) => ({
              display: l.desktopName || l.name,
              icon: l.icon,
              path: l.path,
              showOnTopMenu: true,
            }))}
            {...theme}
            menuHeight="70px"
            avatarComponent={
              <Link to="/perfil">
                {profile?.image ? (
                  <Avatar src={profile.image} boxSize={'36px'} />
                ) : (
                  <Box
                    width="36px"
                    height="36px"
                    backgroundColor={'#F5F5F5'}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    borderRadius={100}
                  >
                    <Text
                      align={'center'}
                      textTransform={'capitalize'}
                      fontSize={'20px'}
                      fontWeight={'bold'}
                      color={theme?.colors.primaryColor}
                    >
                      {profile?.fullName.charAt(0)}
                    </Text>
                  </Box>
                )}
              </Link>
            }
            logoWidth="40px"
            boxShadow="rgba(17, 12, 10, 0.1) 0px 30px 50px 0px"
            backgroundColor="rgba(255,255,255,0.95)"
          >
            {isAuthenticated() && (
              <GoBell cursor={'pointer'} onClick={notificationDisclosure.onOpen} fontSize={'24px'} />
            )}
          </TopMenu>
        </Flex>
      )}
      <VStack paddingTop={{ base: basePaddingTop, lg: theme.isNative ? '0px' : '60px' }} overflowY={'hidden'} display={'flex'}>
        {children}
      </VStack>
      {!hideBottomMenu && <>{!theme.isNative && <BottomMenu links={links.filter((l) => !l.hide)} />}</>}
    </OpenBookProvider>
  );
}
